import type { AiCreditMeter as AiCreditMeterModel, AiCreditMeterState } from '@/state/billing/ai-credit-meter';
import { useState } from 'react';
import { cn } from 'helpers/cn';
import { LogoBars, MeterTrack } from '@/components/ai-chat';
import { NavLink } from '@/components/NavLink';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useOrganizations } from '@/state/useOrganizations';
import { useAiCreditMeter } from '@/state/billing/useAiCreditMeter';

/** Every state of the pill has the same box, so it never jumps when the figures arrive. */
const PILL_CLASS =
  'focus-visible:ring-ring flex shrink-0 items-center gap-1.5 rounded-pill border px-2.5 py-1 font-mono text-micro transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none';

/** The pill's colours per level, as the editor's meter: low turns amber, empty red. */
const LEVEL_CLASS = {
  normal: 'border-line bg-field text-dim hover:text-foreground data-[state=open]:text-foreground',
  low: 'border-warn-line bg-warn-bg text-warn',
  empty: 'border-danger-line bg-danger-bg text-danger',
} as const;

/** The pill without figures: what it says to a screen reader, and on hover. */
const NEUTRAL_COPY = {
  loading: { label: 'AI credits: loading', tooltip: null },
  error: { label: 'AI credits could not be read', tooltip: 'AI credits could not be read. Try again in a moment.' },
  none: { label: 'AI credits: no account', tooltip: 'There is no account to show AI credits for.' },
} as const;

/** An empty track: the neutral pill's, so its box matches the meter's. */
const NO_FILL = { percent: 0, level: 'normal' } as const;

type AiCreditMeterProps = { isMobile: boolean };

/**
 * The AI credits left for the account in scope, beside the session counter: the editor's top bar
 * meter, in the header. The same account the session counter reads (an agency's selected client,
 * the caller's own otherwise), and the same meter the editor and the results chat read, so all of
 * them agree. An account on credits sees its ledger; any other (invoice, free, internal) sees the
 * AI's monthly cap. Always on screen (decided 2026-10-20): while it loads, if it cannot be read, and
 * for a caller with no account, it shows a neutral pill of the same size.
 */
export const AiCreditMeter = ({ isMobile }: AiCreditMeterProps) => {
  const { organization } = useOrganizations();
  const state = useAiCreditMeter({ accountId: organization?.id ?? null });
  return <AiCreditMeterView state={state} isMobile={isMobile} />;
};

type AiCreditMeterViewProps = { state: AiCreditMeterState; isMobile: boolean };

export const AiCreditMeterView = ({ state, isMobile }: AiCreditMeterViewProps) =>
  state.status === 'ready' ? (
    <AiCreditMeterPill meter={state.meter} isMobile={isMobile} />
  ) : (
    <NeutralPill status={state.status} isMobile={isMobile} />
  );

type NeutralPillProps = { status: keyof typeof NEUTRAL_COPY; isMobile: boolean };

/** The mark, an empty track and a dash, with nothing to open. Not a button: there is nothing to do. */
const NeutralPill = ({ status, isMobile }: NeutralPillProps) => {
  const { label, tooltip } = NEUTRAL_COPY[status];
  const pill = (
    <span
      role="img"
      aria-label={label}
      aria-busy={status === 'loading' || undefined}
      tabIndex={tooltip ? 0 : undefined}
      data-tour="header-ai-credits"
      className={cn(PILL_CLASS, 'border-line bg-field text-dim')}
    >
      <LogoBars size={11} state="still" />
      <MeterTrack meter={NO_FILL} className={isMobile ? 'h-1 w-6' : 'h-1 w-8'} />
      {!isMobile && (
        <span aria-hidden="true" className="tabular-nums opacity-60">
          –
        </span>
      )}
    </span>
  );
  if (!tooltip) return pill;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="text-body">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

type AiCreditMeterPillProps = { meter: AiCreditMeterModel; isMobile: boolean };

/**
 * The pill and its popover. On a phone it drops the number and keeps the mark and the track: the
 * level still shows, and a tap opens the figures.
 */
export const AiCreditMeterPill = ({ meter, isMobile }: AiCreditMeterPillProps) => {
  const [open, setOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const { view, packs, resets, onCredits } = meter;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={150}>
        {/* The tooltip says what the popover says, so it stays away while the popover is open. */}
        <Tooltip open={tipOpen && !open} onOpenChange={setTipOpen}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={view.label}
                // The platform tour's AI credits step.
                data-tour="header-ai-credits"
                className={cn(PILL_CLASS, LEVEL_CLASS[view.level])}
              >
                <LogoBars size={11} state="still" />
                <MeterTrack meter={view} className={isMobile ? 'h-1 w-6' : 'h-1 w-8'} />
                {!isMobile && <span className="tabular-nums">{view.value}</span>}
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="text-body">
            {view.tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent align="end" sideOffset={8} className="w-64 space-y-2 p-3">
        <p className="m-0 text-body font-medium text-foreground">{view.line}</p>
        {packs && <p className="supporting m-0">{packs}</p>}
        {resets && <p className="supporting m-0">{resets}</p>}
        {/* Only an account on credits has packs to buy. A capped one has nothing to manage there. */}
        {onCredits && (
          <NavLink
            to="/settings/billing"
            onClick={() => setOpen(false)}
            className="text-accent focus-visible:ring-ring inline-block pt-1 text-micro font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            Manage plan
          </NavLink>
        )}
      </PopoverContent>
    </Popover>
  );
};
